import { Link, useLocation } from '@remix-run/react';
import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import DividerContainer from '~/components/divider-container';
import { useNumFormatter } from '~/utils/format/num-formatter';
import { ClickPosition } from '~/utils/click-position';
import { UserLinks } from './user-link';
import SimpleToast from '~/components/simple-toast';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { usePageActive } from '~/utils/active/use-page-active';
import useReflowClick from '~/utils/use-reflow-click';
import { ViewTracker } from '../view-tracker';
import { ModuleType } from '~/utils/module-type';
import { Avatar } from '~/components/avatar';
import { EPrivacyStatus, EUserGender, IUserDetail } from 'shared/models';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { ELEMENT_TIMING_FMP } from '@lemon8/web-app-shared/component';

import './style.scss';

interface UserDescriptionProps {
  user: IUserDetail;
}

export default function UserDescription(props: UserDescriptionProps): JSX.Element {
  const { user } = props;
  const { privacyStatus, userId, gender, ageGroup, location: locationValue } = user;
  const location = useLocation();
  const { t } = useTranslation();
  const active = usePageActive();
  const isRealUser = useAtomValue($IsMobileUser);

  const { region } = useAtomValue($Locale);

  const formatNumber = useNumFormatter();
  const [toast, setToast] = useState({ show: false, content: '' });

  const followingHref = `${location.pathname}/follow?tabkey=following&region=${region}`;
  const followerHref = `${location.pathname}/follow?tabkey=follower&region=${region}`;

  const activeAction = (enterPosition: ClickPosition): void => {
    active({
      schemaOption: {
        pageType: 'user',
        userId,
      },
      ampExtra: {
        enter_position: enterPosition,
      },
    });
  };

  const onClickFollowingInner = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_position: ClickPosition.user_following,
        page_click_id: userId,
      }),
      reflowBehavior: () => {
        activeAction(ClickPosition.user_following);
      },
    },
    [active, userId],
  );

  const onClickFollowing = useCallback(
    (e: SyntheticEvent) => {
      if (privacyStatus === EPrivacyStatus.PrivateUser) {
        e.preventDefault();
        setToast({
          show: true,
          content: t('privateAccount_profile_toast'),
        });
        return;
      }

      onClickFollowingInner(e);
    },
    [privacyStatus, onClickFollowingInner],
  );

  const onClickFollowerInner = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_position: ClickPosition.user_fans,
        page_click_id: userId,
      }),
      reflowBehavior: () => {
        activeAction(ClickPosition.user_fans);
      },
    },
    [active, userId],
  );

  const onClickFollower = useCallback(
    (e: SyntheticEvent) => {
      if (privacyStatus === EPrivacyStatus.PrivateUser) {
        e.preventDefault();
        setToast({
          show: true,
          content: t('privateAccount_profile_toast'),
        });
        return;
      }

      onClickFollowerInner(e);
    },
    [privacyStatus, onClickFollowerInner],
  );

  const genderValue = useMemo(() => {
    if (gender === EUserGender.Unknown) {
      return false;
    }

    return false;

    // TODO no such text
    return gender === EUserGender.MALE ? t('tag_male') : t('tag_female');
  }, [gender]);

  const ageValue = useMemo(() => {
    const AgeGroup = [
      { label: t('age_20_less'), value: 10 },
      { label: t('age_20'), value: 20 },
      { label: t('age_30'), value: 30 },
      { label: t('age_40'), value: 40 },
      { label: t('age_50'), value: 50 },
      { label: t('age_60_more'), value: 60 },
    ];
    return AgeGroup.filter((item) => item.value === ageGroup)[0]?.label;
  }, [ageGroup]);

  const showLink = useMemo(() => {
    const { thirdLinks } = user;
    return Boolean(thirdLinks.length);
  }, [user]);

  const sendTrackingEvent = (position: ClickPosition): void => {
    if (isRealUser) {
      window.Tea('wap_cta_show', {
        current_page_module: ModuleType.user_homepage_info,
        current_position: position,
      });
    }
  };

  return (
    <div className="user-desc">
      <div className="user-desc-main">
        <Avatar
          elementtiming={ELEMENT_TIMING_FMP}
          className="user-desc-main-avatar"
          src={user.avatar}
          alt={user.nickName}
        />
        <DividerContainer wrapperCls="user-desc-main-info" dividerCls="user-desc-main-info-divider">
          <ViewTracker
            key={`user-following-count-${userId}`}
            onView={() => sendTrackingEvent(ClickPosition.user_following)}
          >
            <Link className="user-desc-main-info-item" to={followingHref} onClick={onClickFollowing}>
              <span>{formatNumber(user.followingCount)}</span>
              <span>{t('follow_count_number')}</span>
            </Link>
          </ViewTracker>
          <ViewTracker key={`user-follower-count-${userId}`} onView={() => sendTrackingEvent(ClickPosition.user_fans)}>
            <Link className="user-desc-main-info-item" to={followerHref} onClick={onClickFollower}>
              <span>{formatNumber(user.followerCount)}</span>
              <span>{t('fans_count_number')}</span>
            </Link>
          </ViewTracker>
          <div className="user-desc-main-info-item">
            <span>{formatNumber(user.dugCount + user.favoredCount)}</span>
            <span>{t('profile_likes_favors_count')}</span>
          </div>
        </DividerContainer>
      </div>
      <div className="user-desc-base">
        <h1 className="user-desc-base-name">{user.nickName}</h1>
        <DividerContainer wrapperCls="user-desc-base-info" dividerCls="user-desc-base-info-divider">
          {genderValue && <span>{genderValue}</span>}
          {ageValue && <span>{ageValue}</span>}
          {locationValue && <span className="location">{locationValue}</span>}
          {showLink && <UserLinks user={user} />}
        </DividerContainer>
        {user.description && <p className="user-desc-base-desc">{user.description}</p>}
      </div>
      <SimpleToast show={toast.show} setShow={(show) => setToast({ show, content: '' })} content={toast.content} />
    </div>
  );
}
